import { InputGroup } from '@chakra-ui/react'
import { Box, Flex, Icon, Input, Text } from '@chakra-ui/react'
import { ChevronDown, ChevronUp, Search } from 'lucide-react'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDropdownStyles } from '~/components/shared/componentStyles/Dropdown.style'
import { useOutsideClick } from '~/hooks/useOutsideClick'
import { TranslationKeys } from '~/locales/i18next'
import { castRef } from '~/utils/refUtils'

import { Menu, MenuOptions } from './Menu'
import { MenuItem, MenuListItem } from './MenuItem'

export type SelectItem<TValue> = MenuItem<TValue> & {
  selectedLabel?: React.ReactNode
  searchLabel?: string
}

type DropdownProps<TValue extends string | number> = {
  placeholder?: string
  selectedIndex?: number
  selectedValue?: TValue
  items: SelectItem<TValue>[]
  onSelect?: (value: TValue, index: number) => string | void
  onScrollEndReached?: () => void
  menuOptions?: MenuOptions
  searchable?: boolean
}

export const DropdownSelect = <TValue extends string | number>({
  placeholder = '',
  selectedIndex,
  selectedValue,
  items = [],
  onSelect,
  onScrollEndReached,
  menuOptions,
  searchable,
}: DropdownProps<TValue>) => {
  const { t } = useTranslation()
  const [internalIndex, setInternalIndex] = useState<number>()
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const styles = useDropdownStyles()

  useOutsideClick({
    ref: castRef(containerRef),
    handler: () => {
      if (isOpen) setIsOpen(false)
    },
  })

  const isSearchable = searchable !== undefined ? searchable : items.length > 10

  const extractTextFromReactNode = (node: React.ReactNode): string => {
    if (typeof node === 'string' || typeof node === 'number') {
      return node.toString()
    } else if (Array.isArray(node)) {
      return node.map(extractTextFromReactNode).join(' ')
    } else if (React.isValidElement(node)) {
      const props = node.props as {
        primaryText?: string
        children?: React.ReactNode
      }
      if (props.primaryText) {
        return props.primaryText
      } else if (props.children) {
        return extractTextFromReactNode(props.children)
      } else {
        return ''
      }
    } else {
      return ''
    }
  }

  const handleSelectedInternal = (value: TValue, _index: number) => {
    const originalIndex = items.findIndex((item) => item.value === value)
    onSelect?.(value, originalIndex)
    setInternalIndex(originalIndex)
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleMenuToggle = () => {
    setIsOpen((prev) => !prev)
    setSearchTerm('')
  }

  const getSelectedItemIndex = useCallback(
    (value?: TValue, index?: number) => {
      if (value !== undefined) {
        return items.findIndex((item) => item.value === value)
      }
      return index
    },
    [items],
  )

  const filteredItems = useMemo(() => {
    if (!isSearchable || searchTerm.trim() === '') {
      return items
    }
    return items.filter((item) => {
      const label = item.searchLabel ?? extractTextFromReactNode(item.content)
      return label.toLowerCase().includes(searchTerm.toLowerCase())
    })
  }, [items, searchTerm, isSearchable])

  const placeHolderText = useMemo(() => {
    return <Text css={styles.placeholderText}>{placeholder}</Text>
  }, [placeholder])

  const SelectedText = useCallback(() => {
    const resolvedSelectedIndex = getSelectedItemIndex(
      selectedValue,
      selectedIndex ?? internalIndex,
    )
    if (resolvedSelectedIndex === undefined) return placeHolderText
    const selectedItem = items[resolvedSelectedIndex]
    if (!selectedItem) return placeHolderText
    let itemLabel = selectedItem.selectedLabel
    let itemContent = selectedItem.content
    if (typeof itemLabel === 'string') {
      itemLabel = (
        <Text lineClamp={1} css={styles.defaultText}>
          {itemLabel}
        </Text>
      )
    }
    if (typeof itemContent === 'string') {
      itemContent = (
        <Text lineClamp={1} css={styles.defaultText}>
          {itemContent}
        </Text>
      )
    }
    return itemLabel ?? itemContent ?? placeHolderText
  }, [items, selectedIndex, selectedValue, internalIndex])

  const wrappedItems = useMemo(() => {
    return filteredItems.map((item) => {
      if (
        typeof item.content === 'string' ||
        typeof item.content === 'number'
      ) {
        return {
          value: item.value,
          content: (
            <MenuListItem
              key={item.value}
              primaryText={t(item.content.toString() as TranslationKeys)}
            />
          ),
        }
      }
      return {
        value: item.value,
        content: <MenuListItem key={item.value}>{item.content}</MenuListItem>,
      }
    })
  }, [filteredItems])

  return (
    <Flex ref={containerRef} css={styles.container}>
      <Flex
        ref={dropdownRef}
        css={styles.menuButton}
        onClick={handleMenuToggle}
      >
        <Box css={{ px: 1 }}>
          <SelectedText />
        </Box>
        <Icon as={isOpen ? ChevronUp : ChevronDown} css={styles.icon} />
      </Flex>
      {isOpen && (
        <Menu
          header={
            isSearchable ? (
              <InputGroup
                endElement={
                  <Icon color="border.muted" boxSize={4}>
                    <Search size="100%" />
                  </Icon>
                }
              >
                <Input
                  size="sm"
                  placeholder={t('dropdownSelect.placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            ) : undefined
          }
          anchorRef={castRef(dropdownRef)}
          items={wrappedItems}
          onSelect={handleSelectedInternal}
          onScrollEndReached={onScrollEndReached}
          options={menuOptions}
        />
      )}
    </Flex>
  )
}
